import { useState, useRef, useEffect } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useLocation, useNavigate } from "react-router-dom";
import { t } from "../i18n";

type Step = "email" | "otp";

export function LoginPage() {
  const { signIn } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from ?? "/app";

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  const codeRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  // Focus first OTP input when step changes to otp
  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => codeRefs[0].current?.focus(), 100);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      await signIn("resend-otp", { email: email.trim() });
      setEmailSent(true);
      setStep("otp");
    } catch {
      setError(t("loginErrorSend"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    // Only accept digits
    const digit = value.replace(/\D/g, "").slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setError(null);

    // Move to next input
    if (digit && index < 5) {
      codeRefs[index + 1].current?.focus();
    }

    // Auto-submit when all digits are filled
    if (digit && index === 5) {
      const fullCode = [...newCode].join("");
      if (fullCode.length === 6) {
        handleOTPSubmit(fullCode);
      }
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs[index - 1].current?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] ?? "";
      }
      setCode(newCode);
      const focusIdx = Math.min(pasted.length, 5);
      codeRefs[focusIdx].current?.focus();
      if (pasted.length === 6) {
        handleOTPSubmit(pasted);
      }
    }
  };

  const handleOTPSubmit = async (codeString?: string) => {
    const fullCode = codeString ?? code.join("");
    if (fullCode.length < 6 || isLoading) return;

    setIsLoading(true);
    setError(null);

    try {
      await signIn("resend-otp", { email: email.trim(), code: fullCode });
      navigate(from, { replace: true });
    } catch {
      setError(t("loginErrorVerify"));
      setCode(["", "", "", "", "", ""]);
      setTimeout(() => codeRefs[0].current?.focus(), 50);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    setError(null);
    setCode(["", "", "", "", "", ""]);
    try {
      await signIn("resend-otp", { email: email.trim() });
      setEmailSent(true);
    } catch {
      setError(t("loginErrorResend"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="st-page" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <header style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px clamp(18px, 4vw, 48px)",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
      }}>
        <a
          href="/"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            color: "var(--st-ink)",
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: "-0.04em",
            textDecoration: "none",
          }}
        >
          <img
            src="/landing/icon.png"
            alt=""
            style={{ width: 28, height: 28, borderRadius: 6, boxShadow: "0 4px 12px rgba(255,59,142,0.2)" }}
          />
          Stalkie
        </a>
        <a
          href="/"
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--st-muted)",
            textDecoration: "none",
          }}
        >
          {t("loginBackToHome")}
        </a>
      </header>

      {/* Main */}
      <main style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(32px, 6vw, 80px) 20px",
        background: "radial-gradient(circle at 30% 20%, rgba(255,59,142,0.04), transparent 50%), radial-gradient(circle at 80% 80%, rgba(124,58,237,0.03), transparent 50%), #fcf8fa",
      }}>
        <div style={{ width: "100%", maxWidth: 420 }}>

          {/* Card */}
          <div style={{
            background: "#fff",
            border: "1px solid rgba(15,23,42,0.08)",
            borderRadius: 24,
            padding: "clamp(28px, 5vw, 48px)",
            boxShadow: "0 20px 60px -12px rgba(15,23,42,0.1), 0 0 0 1px rgba(255,59,142,0.05)",
          }}>

            {step === "email" ? (
              <>
                {/* Icon */}
                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: "linear-gradient(135deg, #ff3b8e 0%, #e91e7a 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 24,
                  boxShadow: "0 8px 24px rgba(255,59,142,0.25)",
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>

                <h1 style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontSize: "clamp(26px, 5vw, 32px)",
                  fontWeight: 800,
                  color: "var(--st-ink)",
                  letterSpacing: "-0.03em",
                  margin: "0 0 8px",
                  lineHeight: 1.1,
                }}>
                  {t("loginWelcomeBack")}
                </h1>
                <p style={{
                  color: "var(--st-muted)",
                  fontSize: 15,
                  lineHeight: 1.5,
                  margin: "0 0 32px",
                }}>
                  {t("loginSubtitle")}
                </p>

                <form onSubmit={handleEmailSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div>
                    <label
                      htmlFor="login-email"
                      style={{
                        display: "block",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--st-ink)",
                        marginBottom: 6,
                      }}
                    >
                      {t("loginEmailLabel")}
                    </label>
                    <input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(null); }}
                      placeholder={t("loginEmailPlaceholder")}
                      required
                      disabled={isLoading}
                      autoComplete="email"
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "14px 16px",
                        fontSize: 15,
                        fontFamily: "inherit",
                        fontWeight: 500,
                        color: "var(--st-ink)",
                        background: "#fff",
                        border: `1.5px solid ${error ? "#ef4444" : "rgba(15,23,42,0.12)"}`,
                        borderRadius: 12,
                        outline: "none",
                        transition: "border-color 0.2s, box-shadow 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#ff3b8e"; e.target.style.boxShadow = "0 0 0 3px rgba(255,59,142,0.12)"; }}
                      onBlur={(e) => { e.target.style.borderColor = error ? "#ef4444" : "rgba(15,23,42,0.12)"; e.target.style.boxShadow = "none"; }}
                    />
                  </div>

                  {error && (
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#ef4444" }}>
                      {error}
                    </p>
                  )}

                  <button
                    id="login-submit-btn"
                    type="submit"
                    disabled={isLoading || !email.trim()}
                    style={{
                      width: "100%",
                      padding: "15px 24px",
                      fontSize: 15,
                      fontFamily: "inherit",
                      fontWeight: 700,
                      color: "#fff",
                      background: isLoading ? "rgba(15,23,42,0.5)" : "var(--st-ink)",
                      border: "none",
                      borderRadius: 12,
                      cursor: isLoading || !email.trim() ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span style={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          border: "2px solid rgba(255,255,255,0.3)",
                          borderTopColor: "#fff",
                          animation: "spin 0.7s linear infinite",
                          display: "inline-block",
                        }} />
                        {t("loginSendingCode")}
                      </>
                    ) : (
                      t("loginContinueBtn")
                    )}
                  </button>
                </form>

                <p style={{
                  marginTop: 24,
                  padding: "14px 16px",
                  background: "rgba(248,250,252,0.8)",
                  border: "1px solid rgba(15,23,42,0.06)",
                  borderRadius: 10,
                  fontSize: 13,
                  color: "var(--st-muted)",
                  lineHeight: 1.5,
                }}>
                  {t("loginFooterNote")}
                </p>
              </>
            ) : (
              <>
                {/* OTP Step */}
                <button
                  onClick={() => { setStep("email"); setError(null); setCode(["", "", "", "", "", ""]); }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--st-muted)",
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: "inherit",
                    padding: 0,
                    marginBottom: 24,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 5l-7 7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {t("loginBack")}
                </button>

                <div style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: "linear-gradient(135deg, #ff3b8e 0%, #e91e7a 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 24,
                  boxShadow: "0 8px 24px rgba(255,59,142,0.25)",
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="2" width="14" height="20" rx="2" stroke="#fff" strokeWidth="2"/>
                    <path d="M9 12h6M9 16h4" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>

                <h1 style={{
                  fontFamily: "'Bricolage Grotesque', sans-serif",
                  fontSize: "clamp(24px, 5vw, 30px)",
                  fontWeight: 800,
                  color: "var(--st-ink)",
                  letterSpacing: "-0.03em",
                  margin: "0 0 8px",
                  lineHeight: 1.1,
                }}>
                  {t("loginCheckEmail")}
                </h1>
                <p style={{
                  color: "var(--st-muted)",
                  fontSize: 15,
                  lineHeight: 1.5,
                  margin: "0 0 8px",
                }}>
                  {t("loginSentCodeTo")}
                </p>
                <p style={{
                  color: "var(--st-ink)",
                  fontSize: 15,
                  fontWeight: 700,
                  margin: "0 0 32px",
                }}>
                  {email}
                </p>

                {/* OTP inputs */}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "center",
                    marginBottom: 24,
                  }}
                  onPaste={handleCodePaste}
                >
                  {code.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={codeRefs[idx]}
                      id={`otp-digit-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      disabled={isLoading}
                      onChange={(e) => handleCodeChange(idx, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                      style={{
                        width: 48,
                        height: 56,
                        textAlign: "center",
                        fontSize: 22,
                        fontWeight: 800,
                        color: "var(--st-ink)",
                        fontFamily: "inherit",
                        background: digit ? "#fff" : "rgba(248,250,252,0.8)",
                        border: `1.5px solid ${error ? "#ef4444" : digit ? "#ff3b8e" : "rgba(15,23,42,0.12)"}`,
                        borderRadius: 12,
                        outline: "none",
                        transition: "all 0.15s",
                        cursor: "text",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = error ? "#ef4444" : "#ff3b8e";
                        e.target.style.boxShadow = "0 0 0 3px rgba(255,59,142,0.12)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = error ? "#ef4444" : digit ? "#ff3b8e" : "rgba(15,23,42,0.12)";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  ))}
                </div>

                {error && (
                  <p style={{ margin: "0 0 16px", fontSize: 13, fontWeight: 600, color: "#ef4444", textAlign: "center" }}>
                    {error}
                  </p>
                )}

                <button
                  id="otp-submit-btn"
                  onClick={() => handleOTPSubmit()}
                  disabled={isLoading || code.join("").length < 6}
                  style={{
                    width: "100%",
                    padding: "15px 24px",
                    fontSize: 15,
                    fontFamily: "inherit",
                    fontWeight: 700,
                    color: "#fff",
                    background: isLoading ? "rgba(15,23,42,0.5)" : code.join("").length === 6 ? "var(--st-ink)" : "rgba(15,23,42,0.3)",
                    border: "none",
                    borderRadius: 12,
                    cursor: isLoading || code.join("").length < 6 ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  {isLoading ? (
                    <>
                      <span style={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        animation: "spin 0.7s linear infinite",
                        display: "inline-block",
                      }} />
                      {t("loginLoggingIn")}
                    </>
                  ) : (
                    t("loginSubmitBtn")
                  )}
                </button>

                <div style={{ textAlign: "center" }}>
                  <button
                    onClick={handleResend}
                    disabled={isLoading}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      color: "var(--st-muted)",
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      padding: "4px 8px",
                    }}
                  >
                    {emailSent ? t("loginResendCode") : t("loginSendAgain")}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Footer note */}
          <p style={{
            marginTop: 24,
            textAlign: "center",
            color: "var(--st-muted)",
            fontSize: 13,
          }}>
            {t("loginNeedHelp")}{" "}
            <a
              href="mailto:support@stalkie.app"
              style={{ color: "var(--st-ink)", fontWeight: 600, textDecoration: "none" }}
            >
              {t("loginContactSupport")}
            </a>
          </p>
        </div>
      </main>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type="text"]::-webkit-outer-spin-button,
        input[type="text"]::-webkit-inner-spin-button { -webkit-appearance: none; }
      `}</style>
    </div>
  );
}
